
import React, { useState, useMemo, useEffect } from 'react';
import { DocumentCopyItem } from './types';
import DocumentCopyModal from './DocumentCopyModal';
import { DocumentCopyModalMode } from './DocumentCopyModal';
import './VariousDocuments.css';
import { API_BASE_URL } from '../../../../../../../config';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface VariousDocumentsProps {
  isOpen: boolean;
  onToggle: () => void;
  /** Initial document items (e.g. from DB). If not provided, default list is used. */
  value?: DocumentCopyItem[];
  /** Called when checkbox state changes, so parent can persist to DB */
  onChange?: (items: DocumentCopyItem[]) => void;
  onDocumentCreated?:()=>void;
  loading?: boolean;
  error?: string | null;
  /** Locked codes cannot be unchecked (invitation edit). Value = short reason label. */
  lockedCodes?: Record<string, string>;
  /** When false, hide catalog add/edit (新規追加・説明) — e.g. invitation edit mode. */
  allowCatalogEdit?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const VariousDocuments: React.FC<VariousDocumentsProps> = ({
  isOpen,
  onToggle,
  value,
  onChange,
  onDocumentCreated,
  loading = false,
  error = null,
  lockedCodes = {},
  allowCatalogEdit = true,
}) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [items, setItems] = useState<DocumentCopyItem[]>(() => value ?? []);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<DocumentCopyModalMode>('edit');
  const [editingItem, setEditingItem] = useState<DocumentCopyItem | null>(null);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (value && value.length > 0) {
      setItems(value);
    }
  }, [value]);

  // ---------------------------------------------------------------------------
  // Derived: split items for left/right columns (API list uses half split)
  // ---------------------------------------------------------------------------

  const half = Math.ceil(items.length / 2);
  const displayLeft = items.slice(0, half) ;
  const displayRight = items.slice(half) ;

  // ---------------------------------------------------------------------------
  // Handlers – checkbox and list actions
  // ---------------------------------------------------------------------------
  const handleCheckChange = (id: string) => {
    const current = items.find((i) => i.id === id);
    if (current?.checked && lockedCodes[id]) {
      return;
    }
    const next = items.map((item) =>
      item.id === id ? { ...item, checked: !item.checked } : item
    );
    setItems(next);
    onChange?.(next);
  };

  // ----------
  const handleExplainClick = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (item) {
      setEditingItem(item);
      setModalMode('edit');
      setModalOpen(true);
    }
  };

  // ----------
  const handleAddNew = () => {
    setEditingItem(null);
    setModalMode('add');
    setModalOpen(true);
  };

  // ----------
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  // ----------
  const handleModalSave = async (payload: import('./DocumentCopyModal').DocumentCopyModalSavePayload) => {
    const body = {
      nameJa: payload.nameJa,
      nameEn: payload.nameEn ?? null,
      nameVi: payload.nameVi ?? null,
      descriptionJa: payload.descriptionJa ?? null,
      descriptionEn: payload.descriptionEn ?? null,
      descriptionVi: payload.descriptionVi ?? null,
    };
    if (modalMode === 'add') {
      const res = await fetch(`${API_BASE_URL}/api/various-copies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        return;
      }
      onDocumentCreated?.();
      handleModalClose();
    } else if (modalMode === 'edit' && editingItem) {
      const res = await fetch(`${API_BASE_URL}/api/various-copies/${encodeURIComponent(editingItem.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        return;
      }
      onDocumentCreated?.();
      handleModalClose();
    }
  };

  // ----------
  const handleModalDelete = async (id: string) => {
    const res = await fetch(`${API_BASE_URL}/api/various-copies/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      return;
    }
    onDocumentCreated?.();
    handleModalClose();
  };

  // ----------
  const allChecked = items.length > 0 && items.every((i) => i.checked);

  const handleHeaderCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const next = items.map((item) => ({
      ...item,
      checked: item.checked && lockedCodes[item.id] ? true : !allChecked,
    }));
    setItems(next);
    onChange?.(next);
  };

  const renderRow = (item: DocumentCopyItem) => {
    const lockLabel = lockedCodes[item.id];
    const isLocked = Boolean(item.checked && lockLabel);
    return (
      <div key={item.id} className="various-documents__row">
        <input
          type="checkbox"
          className="various-documents__checkbox"
          checked={item.checked}
          disabled={isLocked}
          title={isLocked ? `${lockLabel}のため解除できません` : undefined}
          onChange={() => handleCheckChange(item.id)}
          id={`various-copy-${item.id}`}
        />
        <label
          htmlFor={`various-copy-${item.id}`}
          className="various-documents__label"
        >
          {item.nameJp}
          {isLocked && (
            <span className="various-documents__lock-badge" title={lockLabel}>
              ロック
            </span>
          )}
        </label>
        {allowCatalogEdit && (
          <button
            type="button"
            className="various-documents__explain-btn"
            onClick={() => handleExplainClick(item.id)}
          >
            説明
          </button>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="collapsible-section collapsible-section--various">
      <button
        type="button"
        className="collapsible-section__header"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <input
          type="checkbox"
          className="collapsible-section__checkbox"
          checked={allChecked}
          onChange={handleHeaderCheckChange}
          onClick={(e) => e.stopPropagation()}
          aria-label="すべて選択"
        />
        <span className="collapsible-section__title">アップロード書類</span>
        <span className={`collapsible-section__chevron ${isOpen ? 'collapsible-section__chevron--open' : ''}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="collapsible-section__body collapsible-section__body--various">
          <div className="various-documents">
            <div className="various-documents__columns">
              <div className="various-documents__column">
                {displayLeft.map((item) => renderRow(item))}
              </div>
              <div className="various-documents__column">
                {displayRight.map((item) => renderRow(item))}
                {allowCatalogEdit && (
                <button
                  type="button"
                  className="various-documents__add-btn"
                  onClick={handleAddNew}
                >
                  新規追加....
                </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <DocumentCopyModal
        isOpen={modalOpen}
        mode={modalMode}
        item={editingItem}
        onClose={handleModalClose}
        onSave={handleModalSave}
        onDelete={handleModalDelete}
      />
    </div>
  );
};

export default VariousDocuments;
export type { DocumentCopyItem };
